import type mapboxgl from 'mapbox-gl';
import {
  type MatchExpression,
  OVERLAY_FILL_LAYER_ID,
  OVERLAY_SOURCE_ID,
  OVERLAY_SYMBOL_LAYER_ID,
  statusIconId,
  statusStyles,
} from './constants.js';
import type { FetcherContext, RoomFeatureCollection } from './types.js';

const ensureStatusImages = async (map: mapboxgl.Map) => {
  await Promise.all(
    (Object.keys(statusStyles) as Array<keyof typeof statusStyles>).map((status) => {
      const imageId = statusIconId(status);
      if (map.hasImage(imageId)) {
        return Promise.resolve();
      }

      const { iconUrl } = statusStyles[status];

      return new Promise<void>((resolve, reject) => {
        map.loadImage(iconUrl, (error, image) => {
          if (error || !image) {
            reject(error ?? new Error(`Unable to load icon for status ${status}`));
            return;
          }

          if (!map.hasImage(imageId)) {
            map.addImage(imageId, image, { pixelRatio: 2 });
          }

          resolve();
        });
      });
    }),
  );
};

const ensureOverlayLayers = (map: mapboxgl.Map) => {
  const fallbackColor = '#ffffff';

  const colorExpression: MatchExpression = [
    'match',
    ['get', 'status'],
    'unavailable_and_occupied',
    statusStyles.unavailable_and_occupied?.fillColor ?? fallbackColor,
    'unavailable_and_free',
    statusStyles.unavailable_and_free?.fillColor ?? fallbackColor,
    'available_and_occupied',
    statusStyles.available_and_occupied?.fillColor ?? fallbackColor,
    statusStyles.available_and_free?.fillColor ?? fallbackColor,
  ];

  if (!map.getLayer(OVERLAY_FILL_LAYER_ID)) {
    map.addLayer({
      id: OVERLAY_FILL_LAYER_ID,
      type: 'fill',
      source: OVERLAY_SOURCE_ID,
      paint: {
        'fill-color': colorExpression as unknown as mapboxgl.Expression,
        'fill-outline-color': '#1f2933',
      },
    });
  }

  if (!map.getLayer(OVERLAY_SYMBOL_LAYER_ID)) {
    map.addLayer({
      id: OVERLAY_SYMBOL_LAYER_ID,
      type: 'symbol',
      source: OVERLAY_SOURCE_ID,
      layout: {
        'icon-image': ['get', 'iconImage'],
        'icon-size': 0.82,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
    });
  }
};

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const syncGeoJsonSource = (
  mapboxMap: mapboxgl.Map,
  featureCollection: RoomFeatureCollection,
  statusesChanged: boolean,
  context: FetcherContext,
) => {
  const featureIds = featureCollection.features.map((feature) => String(feature.id));
  const sortedFeatureIds = [...featureIds].sort();
  const existingSource = mapboxMap.getSource(OVERLAY_SOURCE_ID) as
    | mapboxgl.GeoJSONSource
    | undefined;
  const featureIdsChanged = !arraysEqual(sortedFeatureIds, context.state.visibleFeatureIds);

  if (!existingSource) {
    // Add rooms availability layer to the map explicitly for better performance
    mapboxMap.addSource(OVERLAY_SOURCE_ID, {
      type: 'geojson',
      data: featureCollection,
    });
  } else if (featureIdsChanged || statusesChanged) {
    existingSource.setData(featureCollection);
  }

  context.state.visibleFeatureIds = sortedFeatureIds;
};

export const updateOverlay = async (
  mapboxMap: mapboxgl.Map,
  featureCollection: RoomFeatureCollection,
  statusesChanged: boolean,
  context: FetcherContext,
) => {
  syncGeoJsonSource(mapboxMap, featureCollection, statusesChanged, context);
  await ensureStatusImages(mapboxMap);
  ensureOverlayLayers(mapboxMap);
};

export const clearOverlay = (mapboxMap: mapboxgl.Map, context: FetcherContext) => {
  const existingSource = mapboxMap.getSource(OVERLAY_SOURCE_ID) as mapboxgl.GeoJSONSource;

  if (existingSource) {
    const emptyCollection: RoomFeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };
    existingSource.setData(emptyCollection);
  }

  context.state.visibleFeatureIds = [];
  context.state.previousStatuses = {};
};
